import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Lock,
  LogOut,
  RefreshCw,
  Trash2,
  CalendarClock,
  Users,
  Mail,
  ClipboardList,
  ShieldCheck,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useToast } from "../hooks/use-toast";
import Logo from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "sp_admin_token";
const STATUSES = ["pending", "confirmed", "completed", "cancelled"];
const statusColor = {
  pending: "text-amber-300 border-amber-300/40 bg-amber-300/10",
  confirmed: "text-sky-300 border-sky-300/40 bg-sky-300/10",
  completed: "text-emerald-300 border-emerald-300/40 bg-emerald-300/10",
  cancelled: "text-rose-300 border-rose-300/40 bg-rose-300/10",
};

const Admin = () => {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [vips, setVips] = useState([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const authHeader = { headers: { "X-Admin-Token": token } };

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast({ title: "Welcome back", description: "You're signed in to the admin dashboard." });
    } catch (err) {
      toast({ title: "Access denied", description: "Incorrect password.", variant: "destructive" });
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setPassword("");
  };

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [b, v] = await Promise.all([
        axios.get(`${API}/admin/bookings`, { headers: { "X-Admin-Token": token } }),
        axios.get(`${API}/admin/vip-signups`, { headers: { "X-Admin-Token": token } }),
      ]);
      setBookings(b.data);
      setVips(v.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        logout();
        toast({ title: "Session expired", description: "Please sign in again.", variant: "destructive" });
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(`${API}/admin/bookings/${id}`, { status }, authHeader);
      setBookings((prev) => prev.map((b) => (b.id === id ? data : b)));
      toast({ title: "Status updated", description: `Booking marked ${status}.` });
    } catch (err) {
      toast({ title: "Update failed", variant: "destructive" });
    }
  };

  const deleteBooking = async (id) => {
    try {
      await axios.delete(`${API}/admin/bookings/${id}`, authHeader);
      setBookings((prev) => prev.filter((b) => b.id !== id));
      toast({ title: "Booking deleted" });
    } catch (err) {
      toast({ title: "Delete failed", variant: "destructive" });
    }
  };

  const deleteVip = async (id) => {
    try {
      await axios.delete(`${API}/admin/vip-signups/${id}`, authHeader);
      setVips((prev) => prev.filter((v) => v.id !== id));
      toast({ title: "Signup removed" });
    } catch (err) {
      toast({ title: "Delete failed", variant: "destructive" });
    }
  };

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch {
      return iso;
    }
  };

  // ---------- Login gate ----------
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-5 bg-[#0c1620]">
        <form onSubmit={login} className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#13212e] p-8">
          <div className="flex justify-center mb-6"><Logo /></div>
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[var(--gold)]/10 border border-[var(--gold)]/40">
            <Lock className="text-[var(--gold)]" size={24} />
          </div>
          <h1 className="text-center font-display text-2xl font-extrabold text-white">Admin Access</h1>
          <p className="mt-1 text-center text-sm text-muted-foreground">Enter your passcode to continue.</p>
          <div className="mt-6">
            <Label className="text-sm text-gray-200">Password</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="mt-2 bg-[#0a121a] border-white/15 h-11"
            />
          </div>
          <button type="submit" disabled={loggingIn} className="btn-gold mt-6 w-full rounded-full py-3 text-sm font-semibold uppercase tracking-widest disabled:opacity-60">
            {loggingIn ? "Signing in..." : "Sign In"}
          </button>
        </form>
      </div>
    );
  }

  const pending = bookings.filter((b) => b.status === "pending").length;
  const statCards = [
    { icon: ClipboardList, label: "Total Bookings", value: bookings.length },
    { icon: CalendarClock, label: "Pending", value: pending },
    { icon: Mail, label: "VIP Signups", value: vips.length },
  ];

  // ---------- Dashboard ----------
  return (
    <div className="min-h-screen bg-[#0c1620] px-4 sm:px-8 py-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/10 pb-5">
          <div className="flex items-center gap-3">
            <Logo />
            <span className="hidden sm:inline-flex items-center gap-1 rounded-full border border-[var(--gold)]/40 px-3 py-1 text-xs text-[var(--gold)]">
              <ShieldCheck size={13} /> Admin
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-gray-200 hover:border-[var(--gold)]/50 transition-colors">
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button onClick={logout} className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-gray-200 hover:border-rose-400/50 hover:text-rose-300 transition-colors">
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mt-6">
          {statCards.map((s) => (
            <div key={s.label} className="rounded-2xl border border-white/10 bg-[#13212e] p-5">
              <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest">
                <s.icon size={15} className="text-[var(--gold)]" /> {s.label}
              </div>
              <div className="mt-2 font-display text-3xl font-extrabold text-white">{s.value}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="bookings" className="mt-8">
          <TabsList className="bg-[#13212e] border border-white/10">
            <TabsTrigger value="bookings" className="data-[state=active]:bg-[var(--gold)] data-[state=active]:text-[#0c1620]">
              <ClipboardList size={15} className="mr-2" /> Bookings
            </TabsTrigger>
            <TabsTrigger value="vips" className="data-[state=active]:bg-[var(--gold)] data-[state=active]:text-[#0c1620]">
              <Users size={15} className="mr-2" /> VIP Signups
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bookings" className="mt-5">
            {bookings.length === 0 ? (
              <Empty text="No bookings yet." />
            ) : (
              <div className="overflow-x-auto rounded-2xl border border-white/10">
                <table className="w-full min-w-[820px] text-sm">
                  <thead className="bg-[#0a121a] text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                      {["Ref", "Guest", "Service", "Date", "Pax", "Total", "Status", ""].map((h) => (
                        <th key={h} className="px-4 py-3 font-semibold">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {bookings.map((b) => (
                      <tr key={b.id} className="hover:bg-white/[0.02]">
                        <td className="px-4 py-3 font-semibold text-[var(--gold)] whitespace-nowrap">{b.reference}</td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-white">{b.name}</div>
                          <div className="text-xs text-muted-foreground">{b.email}</div>
                          <div className="text-xs text-muted-foreground">{b.phone}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-200 whitespace-nowrap">{b.service}</td>
                        <td className="px-4 py-3 text-gray-200 whitespace-nowrap">
                          {b.date}{b.time ? ` · ${b.time}` : ""}
                        </td>
                        <td className="px-4 py-3 text-gray-200">{b.passengers}</td>
                        <td className="px-4 py-3 font-semibold text-white whitespace-nowrap">${b.estimated_price}</td>
                        <td className="px-4 py-3">
                          <Select value={b.status} onValueChange={(v) => updateStatus(b.id, v)}>
                            <SelectTrigger className={`h-8 w-32 text-xs capitalize border ${statusColor[b.status] || "border-white/15"}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STATUSES.map((s) => (
                                <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-4 py-3">
                          <button onClick={() => deleteBooking(b.id)} className="text-gray-400 hover:text-rose-400 transition-colors" aria-label="Delete">
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="vips" className="mt-5">
            {vips.length === 0 ? (
              <Empty text="No VIP signups yet." />
            ) : (
              <div className="rounded-2xl border border-white/10 divide-y divide-white/5">
                {vips.map((v) => (
                  <div key={v.id} className="flex items-center justify-between px-5 py-4 hover:bg-white/[0.02]">
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--gold)]/10 border border-[var(--gold)]/30 text-[var(--gold)]">
                        <Mail size={16} />
                      </div>
                      <div>
                        <div className="text-white font-medium">{v.email}</div>
                        <div className="text-xs text-muted-foreground">{fmt(v.created_at)}</div>
                      </div>
                    </div>
                    <button onClick={() => deleteVip(v.id)} className="text-gray-400 hover:text-rose-400 transition-colors" aria-label="Delete">
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

const Empty = ({ text }) => (
  <div className="rounded-2xl border border-dashed border-white/15 bg-[#13212e] p-12 text-center text-muted-foreground">
    {text}
  </div>
);

export default Admin;
